"use client";

import { globalStyleObj } from "@/app/assets/styles";
import {
  CommonTextInputField,
  LanguageTabs,
  PageRefresh,
  SubmitButton,
  TranslationForm,
} from "@/components";
import { useErrorToast, useGenerateSlug, useSuccessToast } from "@/lib/hooks";
import { FAQCategoryValidationSchema } from "@/schemas/pagesSchema/faq/faqCategorySchema";
import { useAppSelector } from "@/store/hooks";
import { getCustomTheme } from "@/utils/colors";
import { usePageRefresh } from "@/utils/refreshPage";
import { getTranslationDetails } from "@/utils/translation-helper";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MdCategory } from "react-icons/md";

const FAQCategoryForm = ({
  userId,
  languages = [],
  itemDetails = {},
  translationDetails = {},
  createCategoryFnc = () => {},
  updateCategoryFnc = () => {},
}) => {
  const [activeTab, setActiveTab] = useState("en");
  const { isPending, pageRefresh } = usePageRefresh();
  // Custom Colors
  const { layoutThemePrimaryColorType } = useAppSelector(
    (state) => state.layout
  );
  const { bgColor, hoverBgColor, textColor, hexCode } = useMemo(
    () => getCustomTheme({ layoutThemePrimaryColorType }),
    [layoutThemePrimaryColorType]
  );
  const isItemDetailsExist = Object.keys(itemDetails).length > 0;
  const activeLanguages = languages.filter((lang) => lang.status);

  // React Hook Form Instance
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: zodResolver(FAQCategoryValidationSchema),
    defaultValues: {
      name: getTranslationDetails(translationDetails, "en")?.name || "",
      slug: itemDetails?.slug || "",
      order_number: itemDetails?.order_number
        ? `${itemDetails.order_number + ""}`
        : "1",
    },
  });
  const watchedName = watch("name");

  // Relevant fields for comparison with categoryDetails (For Update Category)
  const relevantFields = {
    name: getTranslationDetails(translationDetails, "en")?.name || "",
    slug: itemDetails?.slug || "",
    order_number: itemDetails?.order_number || "1",
  };
  const hasChanges = !isEqual(relevantFields, watch());

  // Handle Slug value according to the category name
  useEffect(() => {
    const generateSlugWithCategoryName = () => {
      if (watchedName && watchedName.length > 0) {
        setError("slug", "");
        const generatedSlug = useGenerateSlug(watchedName);
        setValue("slug", generatedSlug);
      } else {
        setValue("slug", "");
      }
    };

    if (!isItemDetailsExist) {
      generateSlugWithCategoryName();
    }
  }, [watchedName, setValue]);

  // Handle Validation Errors
  const handleValidationErrors = useCallback(
    (errors) => {
      Object.keys(errors).forEach((field) => {
        setError(field, {
          type: "server",
          message: errors[field].message,
        });
      });
    },
    [setError]
  );

  // Handle Create New Category functionality
  const onSubmit = async (data) => {
    if (!isItemDetailsExist) {
      const createResponse = await createCategoryFnc(data, userId);

      if (createResponse.success) {
        useSuccessToast(createResponse.message);
        reset();
      } else {
        if (createResponse.errors) {
          handleValidationErrors(createResponse.errors);
        } else {
          useErrorToast(createResponse.message);
        }
      }
    } else {
      const updateResponse = await updateCategoryFnc(
        userId,
        itemDetails._id,
        data
      );

      if (updateResponse.success) {
        useSuccessToast(updateResponse.message);
        pageRefresh();
      } else {
        if (updateResponse.errors) {
          handleValidationErrors(updateResponse.errors);
        } else {
          useErrorToast(updateResponse.message);
        }
      }
    }
  };

  // Handle Translation Form Submit
  const handleTranslationSubmit = async (translateData) => {
    const data = {
      ...watch(),
      translateData: { ...translateData, lang: activeTab },
    };

    await onSubmit(data);
  };

  // Handle Refresh Page Loading when update API runs
  if (isPending) {
    return <PageRefresh />;
  }

  return (
    <div
      className={`${globalStyleObj.backgroundLight900Dark300} mt-[40px] rounded-sm shadow-light overflow-hidden`}
    >
      {isItemDetailsExist && activeLanguages.length > 1 && (
        <LanguageTabs
          languages={activeLanguages}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
        />
      )}

      {/* Create Form Fields */}
      {activeTab === "en" ? (
        <form onSubmit={handleSubmit(onSubmit)} className={`p-3 sm:p-5`}>
          {/* Category Name */}
          <CommonTextInputField
            fieldName="name"
            fieldType="text"
            fieldId={`${isItemDetailsExist ? "update-faq-category-name" : "faq-category-name"}`}
            control={control}
            errors={errors}
            errorsType={errors?.name}
            placeholderText="Enter category name"
            labelName="Name"
            labelStatus={true}
            translateField={isItemDetailsExist}
          />

          {/* Category Slug */}
          <CommonTextInputField
            fieldName="slug"
            fieldType="text"
            fieldId={`${isItemDetailsExist ? "update-faq-category-slug" : "faq-category-slug"}`}
            control={control}
            errors={errors}
            errorsType={errors?.slug}
            placeholderText="Enter Slug"
            labelName="Slug"
            labelStatus={true}
            extraClass="mt-5"
          />

          {/* Category Order Number */}
          <CommonTextInputField
            fieldName="order_number"
            fieldType="text"
            fieldId={`${isItemDetailsExist ? "update-faq-category-order-number" : "faq-category-order-number"}`}
            control={control}
            errors={errors}
            errorsType={errors?.order_number}
            placeholderText="Order Number"
            labelName="Order Number"
            labelStatus={false}
            extraClass="mt-5"
            extraInformationText="Use higher number for higher order. Default is 1."
          />

          <SubmitButton
            isSubmitting={isSubmitting}
            hasChanges={hasChanges}
            isDetailsExist={isItemDetailsExist}
            colorGrade={{
              bgColor,
              hoverBgColor,
              textColor,
              hexCode,
            }}
            label={isItemDetailsExist ? "Update Category" : "Create Category"}
            icon={<MdCategory />}
          />
        </form>
      ) : (
        <TranslationForm
          fields={[
            {
              name: "name",
              lableName: "Name",
              value:
                getTranslationDetails(translationDetails, activeTab)?.name ||
                "",
              placeholderText: "Enter Category Name",
              fieldId: `translate-${activeTab}-update-faq-category-name`,
            },
          ]}
          isSubmitting={isSubmitting}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
          handleTranslationSubmit={handleTranslationSubmit}
        />
      )}
    </div>
  );
};

export default FAQCategoryForm;
